/**
 * Error Logging Service for Wayo Frontend
 * Captures and sends errors to the backend for centralized logging
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

class ErrorLogger {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.userId = null;
    this.userEmail = null;
  }

  /**
   * Set user context for error tracking
   */
  setUser(userId, userEmail) {
    this.userId = userId;
    this.userEmail = userEmail;
  }

  /**
   * Clear user context on logout
   */
  clearUser() {
    this.userId = null;
    this.userEmail = null;
  }

  /**
   * Log an error to the backend
   */
  async logError(error, extra = {}) {
    const errorData = {
      message: error.message || String(error),
      stack: error.stack || null,
      component: extra.component || null,
      url: window.location.href,
      user_agent: navigator.userAgent,
      user_id: this.userId,
      user_email: this.userEmail,
      extra: {
        ...extra,
        timestamp: new Date().toISOString(),
        pathname: window.location.pathname,
        referrer: document.referrer || null
      }
    };

    // Add to queue
    this.queue.push(errorData);

    // Process queue
    this.processQueue();
  }

  /**
   * Process queued errors
   */
  async processQueue() {
    if (this.isProcessing || this.queue.length === 0) return;

    this.isProcessing = true;

    while (this.queue.length > 0) {
      const errorData = this.queue.shift();

      try {
        await fetch(`${API_URL}/api/errors/log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(errorData)
        });
      } catch (e) {
        // If logging fails, log to console
        console.error('Failed to log error to server:', e);
        console.error('Original error:', errorData);
      }
    }

    this.isProcessing = false;
  }

  /**
   * Capture unhandled promise rejections
   */
  captureUnhandledRejections() {
    window.addEventListener('unhandledrejection', (event) => {
      this.logError(event.reason || new Error('Unhandled Promise Rejection'), {
        type: 'unhandledrejection'
      });
    });
  }

  /**
   * Capture global errors
   */
  captureGlobalErrors() {
    window.addEventListener('error', (event) => {
      // Ignore script loading errors from external sources
      if (event.filename && !event.filename.includes(window.location.origin)) {
        return;
      }

      this.logError(event.error || new Error(event.message), {
        type: 'global_error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });
  }

  /**
   * Initialize error capturing
   */
  init() {
    this.captureGlobalErrors();
    this.captureUnhandledRejections();
    console.log('Error logging initialized');
  }
}

// Create singleton instance
const errorLogger = new ErrorLogger();

export default errorLogger;

// Convenience function for manual error logging
export const logError = (error, extra = {}) => errorLogger.logError(error, extra);
export const setErrorUser = (userId, userEmail) => errorLogger.setUser(userId, userEmail);
export const clearErrorUser = () => errorLogger.clearUser();
